import clsx from "clsx"
import gsap from "gsap"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { FC, useEffect, useMemo, useState } from "react"
import {
  useGetMineInfoQuery,
  useGetUserProgressMineGameQuery
} from "store/apis/mineGameAPI/mine-game.api"
import useServerTimer from "hooks/useServerTimer"
import { useAnimatedNumber } from "hooks/useAnimatedNumber"
import { isToday } from "helpers/isToday"
import { ServerDate } from "helpers/server-date.class"
import { hapticFeedback } from "utils/hapticFeedback"
import { Light } from "./ui/light"
import { MineBrokeIcon, MineIcon } from "assets/icons"
import styles from "./index.module.scss"

interface ISideMenuMine {}

export const SideMenuMine: FC<ISideMenuMine> = () => {
  const { t, i18n } = useTranslation()

  const [activePlayers, setActivePlayers] = useState<number>(0)

  const { hours, minutes, seconds, restart } = useServerTimer({
    expiryTimestamp: new ServerDate(),
    autoStart: false
  })

  const {
    data: mineInfo,
    isLoading: mineInfoLoading,
    isFetching: mineIsFetching
  } = useGetMineInfoQuery()
  const {
    data: mineGameProgress,
    isLoading: userProgressLoading,
    isFetching: userProgressFetching
  } = useGetUserProgressMineGameQuery()

  const time = useMemo(() => {
    if (hours === 0)
      return `${minutes}${t("minutes")} ${seconds}${t("seconds")}`

    return `${hours}${t("hours")} ${minutes}${t("minutes")}`
  }, [hours, minutes, seconds])

  const redirectToGame = useMemo(() => {
    if (userProgressLoading || userProgressFetching || !mineGameProgress)
      return false

    return (
      (mineGameProgress.inGame && !mineGameProgress.finishSessionAt) ||
      (mineGameProgress.startSessionAt &&
        mineGameProgress.finishSessionAt &&
        new ServerDate(mineGameProgress.startSessionAt).getTime() <
          new ServerDate(mineGameProgress.finishSessionAt).getTime())
    )
  }, [mineGameProgress, userProgressLoading, userProgressFetching])

  const mineProgress = useMemo(() => mineInfo?.health || 0, [mineInfo?.health])

  const isTodayUnavailable = useMemo(() => {
    const finishSessionAt = mineGameProgress?.finishSessionAt
    return finishSessionAt ? isToday(finishSessionAt) : false
  }, [mineGameProgress?.finishSessionAt])

  const isMineBroken = useMemo(() => {
    return Boolean(mineInfo?.brokeAt)
  }, [mineInfo?.brokeAt])

  const bgLineColor = useMemo(() => {
    return gsap.utils.interpolate([
      "#FF8080",
      "#FF8080",
      "#FFB47F",
      "#FFD180",
      "#FFD180",
      "#CCBE98",
      "#CCBE98"
    ])((mineProgress || 1) / 100 || 1)
  }, [mineProgress])

  const mineProgressAnimated = useAnimatedNumber(mineProgress, {
    duration: 0.5,
    fractionDigits: 2
  })

  useEffect(() => {
    if (
      mineInfoLoading ||
      mineIsFetching ||
      !mineInfo ||
      mineInfo?.activePlayers == null
    ) {
      return
    }

    setActivePlayers(mineInfo.activePlayers)
  }, [mineInfo, mineInfoLoading, mineIsFetching])

  useEffect(() => {
    if (!mineInfo?.reclaimAt) return

    restart(new ServerDate(mineInfo.reclaimAt))
  }, [mineInfo?.reclaimAt])

  return (
    <Link
      id="btn-mine"
      to={redirectToGame ? "/mine/game" : "/mine/entrance"}
      className={clsx(
        styles.SideMenuMine,
        styles[i18n.language],
        isTodayUnavailable && styles.disabled
      )}
      onClick={() => {
        hapticFeedback()
      }}
    >
      <img src={MineIcon} />

      {isMineBroken ? (
        <img className={clsx(styles.SideMenuMine_broke)} src={MineBrokeIcon} />
      ) : (
        <>
          <div
            className={clsx(
              styles.SideMenuMine_light,
              styles[mineInfo?.condition]
            )}
          >
            <Light />
          </div>
          <div className={clsx(styles.SideMenuMine_people)}>
            {activePlayers}
          </div>
        </>
      )}

      <div
        className={clsx(styles.SideMenuMine_status)}
        style={{
          background: bgLineColor
        }}
      >
        {isMineBroken ? <>{time}</> : <>{mineProgressAnimated}%</>}
      </div>
    </Link>
  )
}
